import { DataRole } from '@mode-switch/express';
import { Attr, Field, Model, Sort } from '@mode-switch/model';
import { EMPTY_ENCODING, EncField, EncMarks, Encoding, EncSort, SortType } from '@mode-vegas/vegas';
import { Switch } from '@mode/shared/util-switch';
import { EncodingHelpers } from '../..';

import { safeMapValues } from './value';

type SortOptions = {
  addDefault: boolean;
};

const ENC_SORT_PREFIX = '@ENC_SORT';
function makeEncSortKey<R extends DataRole>(sort: Sort<R>): string {
  return `${ENC_SORT_PREFIX}|${sort.field.alias}`;
}

/**
 * @description Adds a sort to the sort map. Default sorts are keyed by field alias,
 * whereas sorts of the encoding are keyed with a common prefix + field alias so we
 * can distinguish between them
 */
function addSort<R extends DataRole>(
  sorts: Map<string, Sort<R>>,
  sort: Sort<R>,
  isDefault = false
): Map<string, Sort<R>> {
  if (isDefault) {
    return sorts.set(sort.field.alias, sort);
  } else {
    return sorts.set(makeEncSortKey(sort), sort);
  }
}

export function makeSort<R extends DataRole>(
  model: Model<R>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<R>>,
  sorts: Map<string, Sort<R>>,
  encField: EncField,
  sortOptions: SortOptions
): Map<string, Sort<R>> {
  if (encField.id == null) {
    throw new Error(`Field ${encField} has no id`);
  }

  const field = fields.get(encField.id);
  if (field == null) {
    return sorts;
  }

  let encSort: EncSort | undefined;
  // Sorts of the Encoding are only applied to Discrete fields
  if (field.type === Attr.Type.Discrete) {
    encSort = encoding.sorts.find((sort) =>
      sort.type === SortType.NESTED
        ? // (Field Level) Nested sorts are by alias
          sort.field === field.alias
        : // (View Level) Everything else is by formula
          sort.source === field.formula.source
    );
  }

  if (encSort === undefined) {
    if (sortOptions.addDefault) {
      return addSort(sorts, Sort.ByData.make(field), true);
    }
    return sorts;
  }

  const order = encSort.descending ? Sort.Order.Descending : Sort.Order.Ascending;
  switch (encSort.type) {
    case SortType.ALPHA: {
      return addSort(sorts, Sort.ByAlpha.make(field, order));
    }
    case SortType.DATA: {
      return addSort(sorts, Sort.ByData.make(field, order));
    }
    case SortType.MANUAL: {
      const items = encSort.items;
      if (!Array.isArray(items)) {
        throw Error('items must be defined for a Manual sort');
      }

      const sortItems = safeMapValues(items, field.formula.type);
      return addSort(sorts, Sort.ByManual.make(field, sortItems, order));
    }
    case SortType.FIELD: {
      const byEncField = encSort.byField;
      if (byEncField == null) {
        throw Error('byField must be defined for a ByField sort');
      }

      const byFormula = byEncField.formula;
      const byAttrType = Switch.Compat.fromVegasVarType(byEncField.variable.type);
      const byField = Switch.field(model, viewName, byFormula, byAttrType) as Field<R>;

      if (byField == null) {
        throw Error(`field does not exist for ${byFormula}`);
      }

      return addSort(sorts, Sort.ByField.make(field, byField, order));
    }
    case SortType.NESTED: {
      const byEncField = encSort.byField;
      if (byEncField == null) {
        throw Error('byField must be defined for a ByNested sort');
      }

      const byFormula = byEncField.formula;
      const byAttrType = Switch.Compat.fromVegasVarType(byEncField.variable.type);
      const byField = Switch.field(model, viewName, byFormula, byAttrType) as Field<R>;

      if (byField == null) {
        throw Error(`field does not exist for ${byFormula}`);
      }

      return addSort(sorts, Sort.ByNested.make(field, byField, order));
    }
  }

  // @ts-expect-error unreachable code once we've covered the sort types. If we haven't
  // covered the sort types, this is valid and TS will gripe about an unused directive..
  // letting you know you have a new case to cover
  throw new Error(`Unknown EncSort Type ${encSort.type}`);
}

export function makeSorts<R extends DataRole>(
  model: Model<R>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<R>>,
  sortOptions: SortOptions
): Sort<R>[] {
  const { column, x, x2, row, y, y2, marks } = encoding;

  let sorts = new Map<string, Sort<R>>();

  if (marks.channel === 'x') {
    for (const encField of [...row, ...y, ...y2, ...column, ...x, ...x2]) {
      sorts = makeSort(model, viewName, encoding, fields, sorts, encField, sortOptions);
    }
  } else {
    for (const encField of [...column, ...x, ...x2, ...row, ...y, ...y2]) {
      sorts = makeSort(model, viewName, encoding, fields, sorts, encField, sortOptions);
    }
  }

  switch (marks.channel) {
    case 'x':
    case 'y': {
      for (const encSeries of encoding.marks.series) {
        sorts = makeMarkSorts(model, viewName, encoding, fields, sorts, encSeries.properties, sortOptions);
      }
      break;
    }
    case '*': {
      sorts = makeMarkSorts(model, viewName, encoding, fields, sorts, encoding.marks.properties, sortOptions);
      break;
    }
  }

  /**
   * Remove:
   * - NAMES, unless it's from the encoding (EncSort)
   *
   * Keep:
   * - Any sort from the encoding (EncSort) as it is a user defined sort and not a "default" sort
   * - Dimensions
   */
  const sorted = Array.from(sorts.entries())
    .filter(([key, sort]) => {
      return (
        // Always keep user defined sorts from the encoding
        key.startsWith(ENC_SORT_PREFIX) ||
        // Otherwise, keep Dimensions
        (sort.field.role === Attr.Role.Dimension &&
          // .. but drop NAMES
          sort.field.source !== Attr.Source.Names)
      );
    })
    .map(([, sort]) => {
      return sort;
    });

  return sorted;
}

export function makeTableSorts<R extends DataRole>(
  model: Model<R>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<R>>
): Sort<R>[] {
  if (encoding.marks.channel !== '*') {
    throw new Error(`Expected tabular encoding, saw: ${encoding.marks.channel}`);
  }

  if (encoding.sorts.length === 0) {
    return [];
  }

  const encFields = EncodingHelpers.collectEncFields(encoding);
  const batch = EncodingHelpers.batchEncoding(EMPTY_ENCODING);

  for (const sort of encoding.sorts) {
    const matchingEncField = encFields.find((f) => f.formula === sort.source);
    if (matchingEncField) {
      // Add matching field to the text channel
      batch.addEncFieldToSeries(matchingEncField, 'text', '*');
      // Add to the sorts
      batch.addSort(sort);
    }
  }

  ({ encoding } = batch.complete());
  return makeSorts(model, viewName, encoding, fields, {
    addDefault: false,
  });
}

export function makeMarkSorts<R extends DataRole>(
  model: Model<R>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<R>>,
  sorts: Map<string, Sort<R>>,
  encMarks: EncMarks,
  sortOptions: SortOptions
): Map<string, Sort<R>> {
  const channels = [
    encMarks.color,
    encMarks.size,
    encMarks.text,
    encMarks.label,
    encMarks.detail,
    encMarks.tooltip,
    encMarks.angle,
    encMarks.baseline,
    // encMarks.shape,
    // encMarks.path,
  ];

  for (const channel of channels) {
    for (const encField of channel.values) {
      sorts = makeSort(model, viewName, encoding, fields, sorts, encField, sortOptions);
    }
  }

  return sorts;
}
